import VanillaModal from 'vanilla-modal';
import { ApiService } from '../services/ApiService';
import { AppError } from '../errors/AppError';
import { Component } from './Component';
import { Identity } from './Identity';
import { DeviceDetect } from '../utils/DeviceDetect';
import { Router } from '../router/Router';
import { Exists } from './Exists';
import { TimeoutError } from '../errors/TimeoutError';

/**
 * Polling Component.
 */
export class Polling extends Component {
  /**
   * Selector Modal.
   *
   * @type {string}
   */
  modalSelector = '#modal-1';

  /**
   * Selector for the modal content.
   *
   * @type {string}
   */
  euiPollingSelector = '#eui-polling';

  /**
   * Selector of the modal title.
   *
   * @param {string}
   */
  euiModalTitleSelector = '#eui-modal-title';

  /**
   * Render template.
   *
   * @param {object} template - The template to set in the controller.
   */
  render(template) {
    super.render(template);
    if ((!ApiService.polling || (template && template.in_opt === true)) && ApiService.modaleEnabled !== 'true') {
      this.startProcess();
    } else {
      this.modal = new VanillaModal({
        clickOutside: false,
        transitions: false,
        onOpen: () => {
          // Start the process of polling
          const modalContent = document.querySelector(this.euiPollingSelector);
          if (modalContent) {
            modalContent.focus();
          }
          this.startProcess();
        },
      });

      // Open the modal
      this.modal.open(this.modalSelector);
      document.querySelector(this.euiModalTitleSelector).focus();
    }
  }

  /**
   * Start the process of the polling.
   *
   * @returns {Promise} Resolved promise to chain next process.
   */
  startProcess() {
    return this.createAccount()
      .then((response) => {
        if (response && response.data && response.data.stage === 'exists') {
          return Exists.makeRequest().then(() => false);
        }
        return this.startPolling();
      })
      .then(() => {
        if (this.modal && this.modal.isOpen) {
          // Close the modal if success
          this.modal.close();
        }
      })
      .catch((error) => {
        // Temporary 400002 will not happenned in api
        if (error && error.response && error.response.data && error.response.data.code !== 400002) {
          Router.closeApp({
            redirectUrl: Eui.errorUrl,
          });
        } else {
          let promiseResolve = Promise.resolve();
          const token = ApiService.invitationId;
          switch ((error.response && error.response.status) || error.status) {
            case 408:
              promiseResolve = this.showTimeout();
              // Close the modal if error
              promiseResolve.then(() => {
                if (this.modal && this.modal.isOpen) {
                  this.modal.close();
                }
              });
              break;
            case 302:
            case 205: // For invitation for IE
              if (ApiService.shouldSkipConfirm() && Eui.returnUrl) {
                Router.closeApp({
                  redirectUrl: decodeURIComponent(Eui.returnUrl).replace(/%s/g, token),
                });
              } else {
                AppError.showError(error);
                // Close the modal if error
                promiseResolve.then(() => {
                  if (this.modal && this.modal.isOpen) {
                    this.modal.close();
                  }
                });
              }
              break;
            default:
              if (!(error instanceof Error)) {
                error = new Error(error);
              }
              AppError.showError(error);
              // Close the modal if error
              promiseResolve.then(() => {
                if (this.modal && this.modal.isOpen) {
                  this.modal.close();
                }
              });
              break;
          }
        }
      });
  }

  /**
   * Create account with password.
   *
   * @returns {Promise} Resolved promise to chain next process.
   */
  createAccount() {
    const opts = {
      url: ApiService.apiSchema.polling.create.url,
      method: ApiService.apiSchema.polling.create.method,
      data: {
        password: Identity.passwordValue,
      },
    };

    return this.makeRequest(opts);
  }

  /**
   * Start Polling.
   *
   * @returns {Promise} Resolved promise to chain next process.
   */
  startPolling() {
    const endTime = Number(new Date()) + ((ApiService.polling ? ApiService.polling.timeout : 0) * 1000);

    const checkCondition = (resolve, reject) => {
      this.authAccount().then((response) => {
        if (response && response.status === 205) {
          reject({
            status: response.status,
          });
        }

        if (response && response.status === 200) {
          if (this.modal && this.modal.isOpen) {
            // Close the modal if success
            this.modal.close();
          }

          resolve();

          return;
        }

        if (ApiService.polling && ApiService.polling.timeout && Number(new Date()) >= endTime) {
          if (this.modal && this.modal.isOpen) {
            // Close the modal if timeout
            this.modal.close();
          }

          reject(new TimeoutError());
          return;
        }

        if (response && response.status === 204) {
          // Facade mode
          if (!ApiService.polling) {
            reject(new TimeoutError());

            if (this.modal && this.modal.isOpen) {
              // Close the modal if timeout
              this.modal.close();
            }
            return;
          }

          setTimeout(() => {
            checkCondition(resolve, reject);
          }, (ApiService.polling ? ApiService.polling.freq : 0) * 1000);
        }
      }).catch((err) => {
        reject(err);
      });
    };

    return new Promise((resolve, reject) => {
      setTimeout(() => {
        checkCondition(resolve, reject);
      }, (ApiService.polling ? ApiService.polling.initDelay : 0) * 1000);
    });
  }

  /**
   * Login the account created by Provin.
   *
   * @returns {Promise} Resolved promise to chain next process.
   */
  authAccount() {
    const opts = {
      url: ApiService.apiSchema.polling.auth.url,
      method: ApiService.apiSchema.polling.auth.method,
      data: {
        isMobile: DeviceDetect.isMobile(),
        password: Identity.passwordValue,
      },
    };
    // Use the component makeRequest in order to insert the stage
    return Component.makeRequest(opts);
  }

  /**
   * Show the timeout view.
   *
   * @returns {Promise} Resolved promise to chain next process.
   */
  showTimeout() {
    const opts = {
      url: ApiService.apiSchema.polling.timeout.url,
      method: ApiService.apiSchema.polling.timeout.method,
    };

    Component.updateTracking({ type_page: 'timeout_creation' });

    // Use the component makeRequest in order to insert the stage
    return Component.makeRequest(opts);
  }

  /*
   * Request the signup API and submit the data of the polling
   * @param   {Object} options   body to request
   * @returns {Promise} resolved promise to chain next process
   */
  makeRequest(options) {
    return ApiService.makeRequest({
      url: options.url,
      method: options.method,
      body: options.data,
    }, true);
  }
}
